package com.qrkontaq.utils

import kotlinx.browser.window

/**
 * Thin wrapper over gtag.js and the Meta Pixel (fbq). Both tags are injected
 * from inline head scripts in site/build.gradle.kts and only on
 * qrkontaq.com/www — tenant menu subdomains, the platform-admin host and
 * local dev never get them, so every call here silently no-ops when
 * `window.gtag` / `window.fbq` is absent.
 */
object Analytics {

    private const val CONSENT_KEY = "qk_consent"

    /** null = the visitor hasn't chosen yet; "granted" / "denied" otherwise. */
    val consentChoice: String?
        get() = try {
            window.localStorage.getItem(CONSENT_KEY)
        } catch (_: Throwable) {
            null
        }

    /** True only where the head script actually installed the tag. */
    val isActive: Boolean
        get() = window.asDynamic().gtag != null

    fun grantConsent() {
        try {
            window.localStorage.setItem(CONSENT_KEY, "granted")
        } catch (_: Throwable) {
        }
        updateConsent("granted")
        // Flushes the Meta Pixel's client-side queue (PageView etc.) that
        // accumulated while consent was revoked.
        fbq("consent", "grant")
    }

    fun denyConsent() {
        try {
            window.localStorage.setItem(CONSENT_KEY, "denied")
        } catch (_: Throwable) {
        }
        updateConsent("denied")
        fbq("consent", "revoke")
    }

    /**
     * GA4 recommended `sign_up` event — fired once per successful owner
     * registration; imported into Google Ads as the primary conversion.
     */
    fun signUp(method: String) {
        val params: dynamic = js("{}")
        params.method = method
        gtag("event", "sign_up", params)
        // Meta Ads counterpart — the campaign optimizes on this event.
        val fbParams: dynamic = js("{}")
        fbParams.content_name = method
        fbq("track", "CompleteRegistration", fbParams)
    }

    private fun updateConsent(state: String) {
        val params: dynamic = js("{}")
        params.ad_storage = state
        params.ad_user_data = state
        params.ad_personalization = state
        params.analytics_storage = state
        gtag("consent", "update", params)
    }

    private fun gtag(vararg args: Any?) {
        val g = window.asDynamic().gtag ?: return
        g.apply(null, args)
    }

    private fun fbq(vararg args: Any?) {
        val f = window.asDynamic().fbq ?: return
        f.apply(null, args)
    }
}
